import {useCallback, useContext, useState} from "react";
import {
	onCostChangeProps,
	OnPreviousReceiveCostChangedProps,
	OnPreviousReceiveQuantityChangedProps,
	OnReceivedQuantityChangeProps
} from "../types";
import {
	addOrUpdateReceiveToReceivesList,
	alerts,
	mapCalculatedFieldsToReceive,
	modalClosed,
	receiveStatus,
	sortItemsByModifiedDesc,
	sortItemsByPriorityStockFlag,
	updateLineItemCosts,
	updateLineItemCostsAndQuantity,
} from "../utils/receiveUtilExports";
import {upsertReceive as upsertReceiveApi} from "../../../services/apiHelper";
import {modals} from "../../../util/modalUtil";
import {Receive} from "../../../common/Receive";
import {PurchaseOrder} from "../../../common/PurchaseOrder";
import {PriorityStockItem} from "../../../common/PriorityStockItem";
import {UserContext} from "../../../providers/user";
import {AlertState} from "../../../util/alertUtil";
import {getTotalsFromLineItems} from "../../../util/lineItemUtil";
import {AxiosResponse} from "axios";
import {CurrentScannedProduct, emptyCurrentScannedProduct, LoadingDialog} from "../../../util/emptyObjectsUtil";

export const useReceiveOperations = (
	activeTab: number,
	receive: Receive | undefined,
	setReceive: (receive: Receive | undefined) => void,
	receives: Receive[],
	setReceives: (receives: Receive[]) => void,
	purchaseOrder: PurchaseOrder | undefined,
	setModal: (modal) => void,
	setLoadingDialog: (state: LoadingDialog) => void, 
	priorityStock: PriorityStockItem[],
	enabledPriorityStock: boolean,
	enabledArrivals: boolean,
	setAlertHeader: (state: AlertState) => void,
	setCurrentScannedProduct: (state: CurrentScannedProduct) => void, 
) => {
	const [isExpanded, setIsExpanded] = useState({
		header: true,
		invoiceDetails: true,
	});
	
	const [isHardwareView, setIsHardwareView] = useState<boolean>(true);
	const { graphProfile } = useContext(UserContext);

	const onCloseModal = useCallback(() => {
		setModal(modalClosed);
	}, [setModal]);

	const upsertReceive = useCallback(
		async (receive: Receive): Promise<AxiosResponse<Receive>> => {
			try {
				const updatedReceive = mapCalculatedFieldsToReceive(receive, graphProfile);
				return await upsertReceiveApi(updatedReceive);
			} catch (error) {
				setModal({
					...modals.issueSaving,
					onPrimaryButtonClick: onCloseModal,
				});
				throw error;
			}
		},
		[graphProfile, setModal, onCloseModal]
	);

	const onReceivedQuantityChange = useCallback(
		({ receive: receiveArg, sku, newQuantity }: OnReceivedQuantityChangeProps): Receive => {
			if (!Array.isArray(receiveArg.receiveLineItems) || receiveArg.receiveLineItems.length === 0) {
				return receiveArg;
			}

			let updatedReceive = { ...receiveArg };

			// For EDI receives, preserve the invoicedQuantity and only update receivedQuantity
			const preserveInvoicedQuantity = receiveArg.receiveType === "Edi";

			updatedReceive.receiveLineItems = updatedReceive.receiveLineItems.map((lineItem) =>
				lineItem.sku === sku ? updateLineItemCostsAndQuantity(lineItem, newQuantity, preserveInvoicedQuantity) : lineItem
			);

			const { totalReceivedQuantity, totalInvoicedQuantity } = getTotalsFromLineItems(
				updatedReceive.receiveLineItems
			);

			updatedReceive.totalReceivedQuantity = totalReceivedQuantity;
			updatedReceive.totalInvoicedQuantity = totalInvoicedQuantity;

			if ((enabledPriorityStock || enabledArrivals) && Array.isArray(priorityStock)) {
				sortItemsByPriorityStockFlag(updatedReceive.receiveLineItems, priorityStock);
			} else {
				sortItemsByModifiedDesc(updatedReceive.receiveLineItems);
			}

			return updatedReceive;
		},
		[enabledPriorityStock, enabledArrivals, priorityStock]
	)

	const onCostChange = useCallback(
		({ receive: receiveArg, sku, unitIncCost, unitExCost, lineIncCost, lineExCost }: onCostChangeProps) : Receive => {
			if (!Array.isArray(receiveArg.receiveLineItems) || receiveArg.receiveLineItems.length === 0) {
				return receiveArg;
			}

			let updatedReceive = { ...receiveArg };

			updatedReceive.receiveLineItems = updatedReceive.receiveLineItems.map((lineItem) =>
				lineItem.sku === sku
					? updateLineItemCosts(lineItem, unitIncCost, unitExCost, lineIncCost, lineExCost)
					: lineItem
			);
			
			return updatedReceive;
		},
		[]
	);

	const onPreviousReceiveQuantityChanged = useCallback(
		({ receiveId, sku, newQuantity }: OnPreviousReceiveQuantityChangedProps) => {
			const receive = receives.find((r) => r.id === receiveId);
			if (!receive) return;

			const updatedReceive = onReceivedQuantityChange({ receive, sku, newQuantity });
			setReceives(addOrUpdateReceiveToReceivesList(receives,  updatedReceive));
		},
		[receives, setReceives, onReceivedQuantityChange]
	)
	
	const onPreviousReceiveCostChange = useCallback(
		({ receiveId, sku, unitIncCost, unitExCost, lineIncCost, lineExCost }: OnPreviousReceiveCostChangedProps) => {
			const receive = receives.find((r) => r.id === receiveId);
			if (!receive) return;
			
			const updatedReceive = onCostChange({ receive, sku, unitIncCost, unitExCost, lineIncCost, lineExCost });
			setReceives(addOrUpdateReceiveToReceivesList(receives,  updatedReceive));
		},
		[receives, setReceives, onCostChange]
	)

	const onExpandHeaderChange = useCallback(() => {
		setIsExpanded((prev) => ({
			...prev,
			header: !prev.header,
		}));
	}, []);

	const onInvoiceExpandChange = useCallback(() => {
		setIsExpanded((prev) => ({
			...prev,
			invoiceDetails: !prev.invoiceDetails,
		}));
	}, []);

	const toggleView = useCallback(() => {
		setIsHardwareView((prev) => !prev);
	}, []);

	const onSetInProgressReceive = useCallback(
		async (receiveToEdit: Receive) => {
			if (!receiveToEdit) {
				return;
			}

			setModal(modalClosed);
			const updatedReceive = {
				...receiveToEdit,
				status: receiveStatus.inProgress,
			};

			setLoadingDialog({ isOpen: true, message: "Loading receive..." });
			setReceive(updatedReceive);
			setLoadingDialog({ isOpen: false, message: "" });
			await upsertReceive(updatedReceive);

			const newReceives = addOrUpdateReceiveToReceivesList(receives, updatedReceive);
			setReceive(updatedReceive);
			setReceives(newReceives);
		},
		[upsertReceive, setModal, setReceive, receives,  setReceives, setLoadingDialog]
	);

	const onClearReceive = useCallback(
		async (invoiceNumber: string) => {
			if (!receive || !purchaseOrder) return;

			let updatedReceive = activeTab === 1 ? { ...receive } : receives.find((r) => r.invoiceNumber === invoiceNumber);

			if (!updatedReceive) {
				console.error(`No receive found for invoice number: ${invoiceNumber}`);
				return;
			}

			updatedReceive.status = receiveStatus.cancelled;

			await upsertReceive(updatedReceive);
			
			const newReceives = addOrUpdateReceiveToReceivesList(receives, updatedReceive);
			setReceives(newReceives);

			if (updatedReceive.id === receive.id) {
				setReceive(undefined);
			}

			setModal(modalClosed);
		},
		[activeTab, receive, receives, purchaseOrder, upsertReceive, setReceive, setReceives, setModal]
	);

	const clearCurrentReceive = useCallback(() => {
		setReceive(undefined);
		setIsExpanded((prevState) => ({ ...prevState, invoiceDetails: true }));
		setAlertHeader(alerts.empty);
		setCurrentScannedProduct(emptyCurrentScannedProduct);
	}, [setAlertHeader, setReceive, setCurrentScannedProduct]);

	return {
		isExpanded,
		setIsExpanded,
		isHardwareView,
		setIsHardwareView,
		onReceivedQuantityChange,
		onPreviousReceiveQuantityChanged,
		onCostChange,
		onPreviousReceiveCostChange,
		upsertReceive,
		onExpandHeaderChange,
		onInvoiceExpandChange,
		toggleView,
		onSetInProgressReceive,
		onClearReceive,
		onCloseModal,
		clearCurrentReceive,
	};
};
